import { useState, useEffect } from 'react';
import { Paper, Text, Stack, Group, Button, Alert, List, ThemeIcon, Box, Loader } from '@mantine/core';
import { env } from '../utils/env';
import type { Account } from '../types/account';

// Import SVG icons from assets folder
import checkIcon from '../assets/icons/check.svg';
import lockIcon from '../assets/icons/lock.svg';
import infoCircleIcon from '../assets/icons/info-circle.svg';
import alertCircleIcon from '../assets/icons/alert-circle.svg';

interface PlaidPreLinkMessagingProps {
    accessToken: string;
    onContinue: () => void;
    onCancel: () => void;
}

interface Institution {
    id: string;
    name: string;
    accounts: Account[];
}

const PlaidPreLinkMessaging = ({ accessToken, onContinue, onCancel }: PlaidPreLinkMessagingProps) => {
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch existing institutions on mount
    useEffect(() => {
        const fetchInstitutions = async () => {
            if (!accessToken) return;

            try {
                setLoading(true);
                const response = await fetch(`${env.API_URL}/api/financial/accounts`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch existing accounts');
                }

                const accounts: Account[] = await response.json();

                // Group accounts by institution
                const institutionMap = accounts.reduce((acc, account) => {
                    // The Account type doesn't have institution_id, so we use institution_name instead
                    const institutionKey = account.institution_name || 'Unknown';
                    const institutionName = account.institution_name || 'Unknown Institution';

                    if (!acc[institutionKey]) {
                        acc[institutionKey] = {
                            id: institutionKey,
                            name: institutionName,
                            accounts: []
                        };
                    }

                    acc[institutionKey].accounts.push(account);
                    return acc;
                }, {} as Record<string, Institution>);

                setInstitutions(Object.values(institutionMap));
            } catch (err) {
                console.error('Error fetching institutions:', err);
                setError(err instanceof Error ? err.message : 'Failed to load accounts');
            } finally {
                setLoading(false);
            }
        };

        fetchInstitutions();
    }, [accessToken]);

    return (
        <Paper p="md" radius="md" withBorder>
            <Stack gap="lg">
                <Text size="xl" fw={700} ta="center">Connect Your Bank Account</Text>

                {/* Explain the benefit */}
                <Box>
                    <Text fw={600} size="md" mb="xs">Why connect your account?</Text>
                    <Text size="sm" c="dimmed" mb="md">
                        Connecting your accounts lets Finn automatically track your finances,
                        update your balances, and provide personalized insights without
                        manual data entry.
                    </Text>
                </Box>

                {/* Security messaging */}
                <Alert icon={<img src={lockIcon} alt="Security" width={16} height={16} />} color="teal" title="Bank-level security" radius="md">
                    <Text size="sm">
                        Your credentials are never stored on our servers. We use 256-bit encryption and
                        you can disconnect your accounts at any time.
                    </Text>
                </Alert>

                {/* Show existing institutions */}
                {loading ? (
                    <Box ta="center" py="md">
                        <Loader size="sm" />
                        <Text size="sm" c="dimmed" mt="xs">Loading your existing connections...</Text>
                    </Box>
                ) : institutions.length > 0 ? (
                    <Box>
                        <Text fw={600} size="md" mb="xs">Your existing connections</Text>
                        <Alert icon={<img src={infoCircleIcon} alt="Info" width={16} height={16} />} color="blue" radius="md" mb="sm">
                            You already have the following institutions connected. Adding the same institution again
                            might create duplicate connections.
                        </Alert>
                        <List spacing="xs" size="sm" icon={
                            <ThemeIcon color="blue" size={24} radius="xl">
                                <img src={checkIcon} alt="Check" width={12} height={12} />
                            </ThemeIcon>
                        }>
                            {institutions.map((institution) => (
                                <List.Item key={institution.id}>
                                    <Text fw={500}>{institution.name}</Text>
                                    <Text size="xs" c="dimmed">
                                        {institution.accounts.length} account{institution.accounts.length !== 1 ? 's' : ''}
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    </Box>
                ) : (
                    <Box>
                        <Text size="sm">You haven't connected any accounts yet.</Text>
                    </Box>
                )}

                {/* Error message if any */}
                {error && (
                    <Alert icon={<img src={alertCircleIcon} alt="Error" width={16} height={16} />} color="red" radius="md">
                        {error}
                    </Alert>
                )}

                {/* What to expect */}
                <Box>
                    <Text fw={600} size="md" mb="xs">What to expect</Text>
                    <List size="sm">
                        <List.Item>You'll be securely connected to your financial institution</List.Item>
                        <List.Item>Select which accounts you want to connect</List.Item>
                        <List.Item>Your account information will be updated automatically</List.Item>
                    </List>
                </Box>

                {/* Action buttons */}
                <Group justify="center" mt="md">
                    <Button variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button onClick={onContinue}>
                        Continue to Connect
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
};

export default PlaidPreLinkMessaging; 